import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings2, Upload, Trash2, Save, QrCode } from "lucide-react";
import { toast } from "sonner";

export default function SurveyAdminPanel({ kind, meta, onSaved }) {
  const urlKey = `${kind}_survey_url`, labelKey = `${kind}_survey_label`, qrKey = `${kind}_survey_qr_path`;
  const [url, setUrl] = useState(meta[urlKey] || "");
  const [label, setLabel] = useState(meta[labelKey] || (kind === "opening" ? "Entry survey" : "Exit survey"));
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const save = async (e) => {
    e.preventDefault();
    const clean = url.trim();
    if (clean && !/^https?:\/\//i.test(clean)) return toast.error("URL must start with http:// or https://");
    setBusy(true);
    try {
      await api.put("/programme", { [urlKey]: clean, [labelKey]: label.trim() || undefined });
      toast.success(clean ? "Survey link saved — students can see it now" : "Survey link cleared");
      onSaved?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/admin/surveys/${kind}/qr`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("QR code uploaded");
      onSaved?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeQr = async () => {
    setBusy(true);
    try { await api.delete(`/admin/surveys/${kind}/qr`); toast.success("Uploaded QR removed — showing auto-generated one"); onSaved?.(); }
    catch { toast.error("Could not remove"); }
    finally { setBusy(false); }
  };

  return (
    <section className="border-2 border-[#0F1F2E] bg-[#F7F9FB] p-6 space-y-5" data-testid={`survey-${kind}-admin-panel`}>
      <div>
        <div className="label-eyebrow flex items-center gap-2"><Settings2 size={12} /> Admin · set up this survey</div>
        <p className="text-xs text-[#525252] mt-1 max-w-2xl">Paste the survey link (Microsoft Forms, Google Forms, Qualtrics…) and, optionally, upload the QR image your form tool gave you. If you don't upload one, a QR is generated automatically from the link.</p>
      </div>
      <form onSubmit={save} className="grid gap-3 md:grid-cols-[1fr_200px_auto] items-end">
        <div>
          <Label className="label-eyebrow">Survey URL</Label>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://forms.office.com/…" className="rounded-none h-10 mt-1" data-testid={`survey-${kind}-admin-url`} />
        </div>
        <div>
          <Label className="label-eyebrow">Label</Label>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} className="rounded-none h-10 mt-1" data-testid={`survey-${kind}-admin-label`} />
        </div>
        <Button type="submit" disabled={busy} className="rounded-none h-10 bg-[#0F1F2E] hover:bg-[#1a2f42]" data-testid={`survey-${kind}-admin-save`}>
          <Save size={14} className="mr-2" /> Save link
        </Button>
      </form>
      <div className="flex items-center gap-4 flex-wrap border-t border-[#E5E7EB] pt-4">
        <div className="label-eyebrow flex items-center gap-2"><QrCode size={12} /> QR code image</div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`survey-${kind}-admin-qr-input`} />
        <Button type="button" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} className="rounded-none h-9 border-[#0F1F2E]" data-testid={`survey-${kind}-admin-qr-upload`}>
          <Upload size={14} className="mr-2" /> {meta[qrKey] ? "Replace QR image" : "Upload QR image"}
        </Button>
        {meta[qrKey] && (
          <Button type="button" variant="ghost" disabled={busy} onClick={removeQr} className="rounded-none h-9 text-[#A23232]" data-testid={`survey-${kind}-admin-qr-remove`}>
            <Trash2 size={14} className="mr-2" /> Remove uploaded QR
          </Button>
        )}
        <span className="text-[11px] text-[#525252]">{meta[qrKey] ? "Using your uploaded image." : url ? "Using an auto-generated QR for the link above." : "Save a link first."}</span>
      </div>
    </section>
  );
}
